/**
Project: Voice Panel (c)
Title: User Actions 
Description: User actions for getting/modifying users
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import axios from "axios";
import { includes } from "lodash";
import { AppConfigProps } from "../../../../core/settings/app-config";
import {
  logMessage,
  checkIdentitySession,
  getSessionExpiredError,
} from "../../../../core/actions/common-actions";
import {
  USERS_ROUTING_POLICIES,
  USERS_ALL_RECORDS,
  USERS_DISPLAY_RECORDS,
  USERS_UPDATE_RECORDS,
  USERS_PAGE_RECORDS,
  USER_PHONE_NUMBER_TO_ALTER,
  USER_ROUTING_POLICY_TO_ALTER,
  ALTER_USER_PHONE_NUMBER,
  ALTER_USER_ROUTING_POLICY,
  ALTER_USER_CHECKBOX_STATUS,
  USERS_DATA_RESET,
} from "../../../../store/voice-admin/constants";
import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
import IntlUtil from "../../../../core/helpers/intl-util";

const actionFileName = "users-actions.js";

// Get Users
export function getUsers(policyId, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      let url = "/users";
      if (policyId !== null && policyId.length > 0) {
        url = url + "?policyId=" + policyId;
      }
      await axios
        .get(AppConfigProps.serverRoutePrefix + url, cancelToken)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getUsers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getUsersRoutingPolicies(cancelToken) {
  return async function (dispatch) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/routing-policies?policyStatus=1",
          cancelToken
        )
        .then((res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data &&
            res.data.records.length > 0
          ) {
            let policies = [];
            res.data.records.forEach((policy) => {
              if (policy.policyStatus === 1) {
                policies.push(policy.policyId);
              }
            });
            dispatch({
              type: USERS_ROUTING_POLICIES,
              payload: policies,
            });
          }
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getUsersRoutingPolicies()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            throw err && err.response ? err.response : err;
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  };
}

// Get User Propagation information
export function getUsersPropagation(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix + "/users/propagation",
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getUsersPropagation()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Update Users (Apply Changes)
export function updateUsers(userData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(AppConfigProps.serverRoutePrefix + "/users", userData, cancelToken)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">updateUsers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

// Upload Users (Import Users)
export function uploadUsers(headerData, fileData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          process.env.REACT_APP_SIPPIO_API_SERVER_URL +
            AppConfigProps.serverRoutePrefix +
            "/users",
          fileData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: headerData.authorization,
              RefreshToken: headerData.refreshToken,
            },
          },
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">uploadUsers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function setUsersAllRecords(usersAllRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USERS_ALL_RECORDS,
        payload: usersAllRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setUsersUpdateRecord(usersRecord) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USERS_UPDATE_RECORDS,
        payload: usersRecord,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setUsersDisplayRecords(usersDisplayRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USERS_DISPLAY_RECORDS,
        payload: usersDisplayRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setUsersPageRecords(usersPageRecords) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USERS_PAGE_RECORDS,
        payload: usersPageRecords,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function sortUsers(list, sortColumn, sortOrder) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0) {
      let sortRecords = list.sort((firstItem, secondItem) => {
        switch (sortColumn) {
          case "phoneLicenseStatus":
            if (
              sortOrder === VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING
            ) {
              return (
                secondItem.phoneLicenseStatus - firstItem.phoneLicenseStatus
              );
            } else {
              return (
                firstItem.phoneLicenseStatus - secondItem.phoneLicenseStatus
              );
            }
          //break;

          default:
            if (
              sortOrder === VoiceAdminConstants.TABLE_DATA.SORT_ORDER.ASCENDING
            ) {
              return firstItem[sortColumn].localeCompare(
                secondItem[sortColumn],
                "en",
                {
                  numeric: true,
                }
              );
            } else {
              return secondItem[sortColumn].localeCompare(
                firstItem[sortColumn],
                "en",
                {
                  numeric: true,
                }
              );
            }
          //break;
        }
      });
      resolve(sortRecords);
    } else {
      resolve(list);
    }
  });
}

export function sliceUsers(list, pageOffset, pageLimit) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0 && pageOffset >= 0 && pageLimit > 0) {
      let start = pageOffset * pageLimit;
      let end = start + pageLimit;
      if (end > list.length) end = list.length;

      let sliceRecords = await list.slice(start, end);
      resolve(sliceRecords);
    } else {
      resolve(list);
    }
  });
}

export function searchUsers(list, searchKeyword) {
  return new Promise(async function (resolve, reject) {
    if (
      list &&
      list.length > 0 &&
      searchKeyword &&
      searchKeyword.trim().length > 0
    ) {
      let searchRecords = list.filter((user) => {
        return (
          includes(
            user.name.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(
            user.email.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(
            user.city.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(
            user.country.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          ) ||
          includes(user.phone, searchKeyword.trim()) ||
          includes(
            user.policyId.toLowerCase(),
            searchKeyword.trim().toLowerCase()
          )
        );
      });
      resolve(searchRecords);
    } else {
      resolve(list);
    }
  });
}

export function filterUsers(list, filterSearch) {
  return new Promise(async function (resolve, reject) {
    if (list && list.length > 0 && filterSearch) {
      const getValue = (value) => value.toLowerCase();
      const filterKeys = Object.keys(filterSearch);
      let searchRecords = list.filter((item) => {
        return filterKeys.every((key) => {
          if (!filterSearch[key].length) return true;
          return filterSearch[key].some(
            (filter) => getValue(filter) === getValue(item[key])
          );
        });
      });
      resolve(searchRecords);
    } else {
      resolve(list);
    }
  });
}

export function setUserPhoneNumberToAlter(id) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USER_PHONE_NUMBER_TO_ALTER,
        payload: id,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function setUserRoutingPolicyToAlter(id) {
  return async function (dispatch) {
    try {
      dispatch({
        type: USER_ROUTING_POLICY_TO_ALTER,
        payload: id,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function updateUserPhoneNumber(user) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ALTER_USER_PHONE_NUMBER,
        payload: user,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function updateUserRoutingPolicy(user) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ALTER_USER_ROUTING_POLICY,
        payload: user,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function resetUsersData() {
  return async function (dispatch) {
    try {
      dispatch({
        type: USERS_DATA_RESET,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

// Get Users Data (Export Users)
export function getUsersFileData(intlNamespace, userData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.displayName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.userName").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.city").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.country").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.callingPolicy").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneLicense").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );

  let dataColumns = [];
  if (userData && userData.length > 0) {
    dataColumns = userData.map((user) => {
      let displayName = "";
      if (user.name !== null && user.name.trim().length > 0)
        displayName = user.name.replace(new RegExp(",", "g"), "");
      let userName = "";
      if (user.email !== null && user.email.trim().length > 0)
        userName = user.email.replace(new RegExp(",", "g"), "");
      let city = "";
      if (user.city !== null && user.city.trim().length > 0)
        city = user.city.replace(new RegExp(",", "g"), "");
      let country = "";
      if (user.country !== null && user.country.trim().length > 0)
        country = user.country.replace(new RegExp(",", "g"), "");
      let phoneNumber = "";
      if (user.phone !== null && user.phone.trim().length > 0)
        phoneNumber = user.phone.replace(new RegExp(",", "g"), "");
      let callingPolicy = "";
      if (user.policyId !== null && user.policyId.trim().length > 0)
        callingPolicy = user.policyId.replace(new RegExp(",", "g"), "");
      let phoneLicense = "";
      if (user.phoneLicenseStatus === 1)
        phoneLicense = IntlUtil.getText(
          intlNamespace,
          "content.phoneLicenseYes"
        );
      else
        phoneLicense = IntlUtil.getText(
          intlNamespace,
          "content.phoneLicenseNo"
        );

      return (
        displayName +
        "," +
        userName +
        "," +
        city +
        "," +
        country +
        "," +
        phoneNumber +
        "," +
        callingPolicy +
        "," +
        phoneLicense
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

// Get Users Propagation update result file
export function getUsersPropagationFileData(intlNamespace, propagationData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.displayName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.userName").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.city").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.country").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneNumber").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.callingPolicy").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.phoneLicense").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.updateStatus").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "," +
      IntlUtil.getText(intlNamespace, "content.errorMessage").replace(
        new RegExp(" ", "g"),
        ""
      ) +
      "\n"
  );

  let userData = [];
  if (propagationData && propagationData.length > 0) {
    propagationData.forEach((propItem) => {
      const userIndex = userData.findIndex(
        (userItem) => userItem.id === propItem.id
      );
      if (userIndex >= 0) {
        // Second Item
        let userItem = userData[userIndex];
        if (
          userData[userIndex].updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_SUCCESS
        ) {
          // If First is Success & Second is Failure
          if (
            propItem.updateStatus ===
            VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
          ) {
            if (propItem.paramName === "Phone") {
              userItem.phone = propItem.paramValue;
            }
            if (propItem.paramName === "Policy") {
              userItem.policyId = propItem.paramValue;
            }
            userItem.updateStatus = IntlUtil.getText(
              intlNamespace,
              "content.failure"
            );
          }
        } else if (
          userData[userIndex].updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
        ) {
          // If First is Failure & Second is Failure
          if (
            propItem.updateStatus ===
            VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
          ) {
            if (propItem.paramName === "Phone") {
              userItem.phone = propItem.paramValue;
            }
            if (propItem.paramName === "Policy") {
              userItem.policyId = propItem.paramValue;
            }
          }
        }
        if (propItem.errorMessage)
          userItem.errorMessage =
            userItem.errorMessage && userItem.errorMessage.length > 0
              ? userItem.errorMessage + " " + propItem.errorMessage
              : propItem.errorMessage;

        userData[userIndex] = userItem;
      } else {
        // First Item
        if (
          propItem.updateStatus ===
          VoiceAdminConstants.USN_DATA_PROPAGATION.ITEM_UPDATE_STATUS_FAILURE
        ) {
          if (propItem.paramName === "Phone") {
            propItem.phone = propItem.paramValue;
          }
          if (propItem.paramName === "Policy") {
            propItem.policyId = propItem.paramValue;
          }
        }
        userData.push(propItem);
      }
    });
  }
  let dataColumns = [];
  if (userData && userData.length > 0) {
    dataColumns = userData.map((user) => {
      let displayName = "";
      if (user.name !== null && user.name.trim().length > 0)
        displayName = user.name.replace(new RegExp(",", "g"), "");
      let userName = "";
      if (user.email !== null && user.email.trim().length > 0)
        userName = user.email.replace(new RegExp(",", "g"), "");
      let city = "";
      if (user.city !== null && user.city.trim().length > 0)
        city = user.city.replace(new RegExp(",", "g"), "");
      let country = "";
      if (user.country !== null && user.country.trim().length > 0)
        country = user.country.replace(new RegExp(",", "g"), "");
      let phoneNumber = "";
      if (user.phone !== null && user.phone.trim().length > 0)
        phoneNumber = user.phone.replace(new RegExp(",", "g"), "");
      let callingPolicy = "";
      if (user.policyId !== null && user.policyId.trim().length > 0)
        callingPolicy = user.policyId.replace(new RegExp(",", "g"), "");
      let phoneLicense = "";
      if (user.phoneLicenseStatus === 1)
        phoneLicense = IntlUtil.getText(
          intlNamespace,
          "content.phoneLicenseYes"
        );
      else
        phoneLicense = IntlUtil.getText(
          intlNamespace,
          "content.phoneLicenseNo"
        );
      let updateStatus = "";
      if (user.updateStatus) updateStatus = user.updateStatus;
      let errorMessage = "";
      if (user.errorMessage)
        errorMessage = user.errorMessage.replace(new RegExp(",", "g"), " ");

      return (
        displayName +
        "," +
        userName +
        "," +
        city +
        "," +
        country +
        "," +
        phoneNumber +
        "," +
        callingPolicy +
        "," +
        phoneLicense +
        "," +
        updateStatus +
        "," +
        errorMessage
      );
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function getSampleUsersFileData(intlNamespace, usersData) {
  let headerColumns = [];
  headerColumns.push(
    IntlUtil.getText(intlNamespace, "content.sampleDownload.userName").replace(
      new RegExp(" ", "g"),
      ""
    ) +
      "," +
      IntlUtil.getText(
        intlNamespace,
        "content.sampleDownload.phoneNumber"
      ).replace(new RegExp(" ", "g"), "") +
      "," +
      IntlUtil.getText(
        intlNamespace,
        "content.sampleDownload.callingPolicy"
      ).replace(new RegExp(" ", "g"), "") +
      "\n"
  );

  let dataColumns = [];
  if (usersData && usersData.length > 0) {
    dataColumns = usersData.map((data) => {
      let email = "";
      if (data.email && data.email.trim().length > 0)
        email = data.email.trim().replace(new RegExp(",", "g"), "");
      let phone = "";
      if (data.phone && data.phone.trim().length > 0)
        phone = data.phone.replace(new RegExp(",", "g"), "");
      let policyId = "";
      if (data.policyId && data.policyId.trim().length > 0)
        policyId = data.policyId.trim().replace(new RegExp(",", "g"), "");

      return email + "," + phone + "," + policyId;
    });
  }

  return headerColumns.join("\n").concat(dataColumns.join("\n"));
}

export function getNumbers(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/users/numbers?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  });
}

// new tenent users api

export function getTenentUsers(userData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/tenant-users",
          userData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getTenentUsers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  });
}

export function tenentUsersUploadBulk(userData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .post(
          AppConfigProps.serverRoutePrefix + "/tenant-users/bulk",
          userData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">tenentUsersUploadBulk()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function tenentUsersUpdateBulk(userData, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .put(
          AppConfigProps.serverRoutePrefix + "/tenant-users/bulk",
          userData,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">tenentUsersUpdateBulk()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function getTenentUsersBulk(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/tenant-users/bulk?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">getTenentUsersBulk()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      reject(getSessionExpiredError());
    }
  });
}

export function geSubscriberAccountNumbers(extractType, cancelToken) {
  return new Promise(async function (resolve, reject) {
    let identitySession = checkIdentitySession();
    if (identitySession === true) {
      await axios
        .get(
          AppConfigProps.serverRoutePrefix +
            `/subscriber-account/numbers?extractType=${extractType}`,
          cancelToken
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          if (axios.isCancel(err)) {
            logMessage(
              AppConfigProps.log.severityLevel.warning,
              actionFileName + ">geSubscriberAccountNumbers()",
              AppConfigProps.httpStatusCode.clientClosedRequest,
              err.message
            );
          } else {
            reject(err.response);
          }
        });
    } else {
      throw getSessionExpiredError();
    }
  });
}

export function updateUserCheckbox(user) {
  return async function (dispatch) {
    try {
      dispatch({
        type: ALTER_USER_CHECKBOX_STATUS,
        payload: user,
      });
    } catch (err) {
      throw Error(err);
    }
  };
}

export function isEmptyOrNull(value) {
  return value === null || value === "";
}
